import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import ReportMetricCard from './ReportMetricCard';

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonthBounds = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return { start: formatLocalDate(start), end: formatLocalDate(today) };
};

const safeParseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export default function KitchenInventoryReport() {
  const monthBounds = useMemo(() => getMonthBounds(), []);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fromDate, setFromDate] = useState(monthBounds.start);
  const [toDate, setToDate] = useState(monthBounds.end);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const fetchKitchenItems = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get('https://serverkf.ccsoftwares.com/kitchenitems');
        setItems(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error(e);
        setError('Unable to load kitchen inventory.');
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchKitchenItems();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [fromDate, toDate, search]);

  const filteredItems = useMemo(() => {
    if (!items.length) return [];

    const normalizedQuery = search.trim().toLowerCase();
    const from = fromDate ? new Date(fromDate) : null;
    if (from) from.setHours(0, 0, 0, 0);
    const to = toDate ? new Date(toDate) : null;
    if (to) to.setHours(23, 59, 59, 999);

    return items
      .map((row) => ({
        ...row,
        inDateObj: safeParseDate(row.indate),
        outDateObj: safeParseDate(row.outdate),
        qtyValue: Number(row.qty || 0),
      }))
      .filter((row) => {
        const matches = normalizedQuery
          ? String(row.itemname || '').toLowerCase().includes(normalizedQuery)
          : true;
        if (!matches) return false;

        if (!from && !to) return true;

        if (!row.inDateObj) return false;
        const rangeFrom = from || new Date(-8640000000000000);
        const rangeTo = to || new Date(8640000000000000);
        const outOrNow = row.outDateObj || new Date(8640000000000000);

        return row.inDateObj <= rangeTo && outOrNow >= rangeFrom;
      });
  }, [items, fromDate, toDate, search]);

  const summary = useMemo(() => {
    const activeItems = filteredItems.filter((row) => !row.outdate);
    const activeCount = activeItems.length;
    const activeQty = activeItems.reduce((sum, r) => sum + (r.qtyValue || 0), 0);
    const finishedCount = filteredItems.filter((row) => row.outdate).length;
    const totalCount = filteredItems.length;
    return { activeCount, activeQty, finishedCount, totalCount };
  }, [filteredItems]);

  const paginated = useMemo(() => {
    if (rowsPerPage === -1) return filteredItems;
    const start = page * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={800} sx={{ mb: 2 }}>
        Kitchen Inventory Report
      </Typography>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Search Item"
              placeholder="Start typing item name"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <ReportMetricCard
                label="Total Items (in range)"
                value={summary.totalCount}
                tooltip="Count of kitchen inventory rows overlapping the selected date range. This is not a gross or net sales amount."
                valueSx={{ fontWeight: 800 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <ReportMetricCard
                label="Active Items"
                value={summary.activeCount}
                tooltip="Count of filtered kitchen inventory rows without an out date. This is not a gross or net sales amount."
                valueSx={{ fontWeight: 800 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <ReportMetricCard
                label="Active Qty (sum)"
                value={summary.activeQty.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                tooltip="Sum of quantities for active filtered kitchen inventory rows. This is a quantity, not a gross or net sales amount."
                valueSx={{ fontWeight: 800 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <ReportMetricCard
                label="Finished Items"
                value={summary.finishedCount}
                tooltip="Count of filtered kitchen inventory rows with an out date. This is not a gross or net sales amount."
                valueSx={{ fontWeight: 800 }}
              />
            </Grid>
          </Grid>

          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: 'var(--brand-primary)' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Item</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Qty</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>In Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Out Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>User</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No kitchen items match your filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((row) => (
                    <TableRow key={row.itemid}>
                      <TableCell>{row.itemname}</TableCell>
                      <TableCell>{row.qty}</TableCell>
                      <TableCell>{row.indate}</TableCell>
                      <TableCell>{row.outdate || '-'}</TableCell>
                      <TableCell>{row.user}</TableCell>
                      <TableCell>
                        {row.outdate ? (
                          <Chip size="small" label="Finished" color="default" />
                        ) : (
                          <Chip size="small" label="Active" color="success" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredItems.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
            sx={{ mt: 2 }}
          />
        </>
      )}
    </Box>
  );
}

